import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useSearch } from './SearchContext';
import Keyvault from './Keyvault';
import config from "../../env.json"
import SearchIcon from '@mui/icons-material/Search';

// import { getVaultSecrets, addSecret, updateSecret, deleteSecret } from '../services/vaultService';
// import Header from '../app/Layout/Header/header';
interface Secret {
  LastUpdated: string;
  id: string;
  name: string;
  value: string;
  type: 'secret' | 'certificate' | 'key';
  parentName: string;
  repoName: string;
}

const VaultDetail: React.FC = () => {
  const { vaultName } = useParams<{ vaultName: string }>();
  const { vaultId } = useParams<{ vaultId: string }>();
  const navigate = useNavigate();
  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState<Partial<Secret>>({ name: '', value: '' });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [pageload, setPageload] = useState<boolean>(false);
  const [viewedSecrets, setViewedSecrets] = useState<Record<string, boolean>>({});
  const { searchText, setSearchText } = useSearch();

  const editFormRef = React.useRef<HTMLDivElement>(null);
  const filteredSecrets = searchText.length >= 3
    ? secrets.filter(
      (secret) =>
        secret.name.toLowerCase().includes(searchText.toLowerCase())
      // secret.value.toLowerCase().includes(searchText.toLowerCase())
    )
    : secrets;
  const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    const formattedDate = date.toLocaleDateString('en-CA'); // YYYY-MM-DD
    const formattedTime = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${formattedDate} ${formattedTime}`;
  };

  useEffect(() => {
    const fetchSecrets = async () => {
      if (!vaultName) return;

      try {
        setLoading(true);
        // TODO: Replace with actual API call
        // const data = await getVaultSecrets(vaultName);
        // setSecrets(data);

        // Mock data for now
        setTimeout(async () => {
          const response = await axios.get(
            config.REACT_APP_ENV_API_BASE + '/KeyvaultControl/keyvault-secret-admin?VaultNm=' + vaultName,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionStorage.getItem('login')}`,
              },
            }
          );
          const vaultData = response?.data?.data?.secrets
          // console.log("line 47", vaultData);
          setSecrets(vaultData);
          // setSecrets([
          //   {
          //     id: '1',
          //     name: 'database-connection',
          //     value: '********',
          //     type: 'secret',
          //     lastUpdated: '2023-07-01T10:30:00Z',
          //   },
          //   {
          //     id: '2',
          //     name: 'api-key',
          //     value: '********',
          //     type: 'key',
          //     lastUpdated: '2023-07-05T14:45:00Z',
          //   },
          //   {
          //     id: '3',
          //     name: 'ssl-cert',
          //     value: '********',
          //     type: 'certificate',
          //     lastUpdated: '2023-06-28T09:15:00Z',
          //   },
          // ]);
          setLoading(false);
        }, 800);
      } catch (err) {
        setError('Failed to fetch vault secrets');
        setLoading(false);
      }
    };

    fetchSecrets();
  }, [vaultName, pageload, vaultId]);

  const handleAddSecret = async () => {
    if (!newSecret.name || !newSecret.value) return;

    try {
      // TODO: Replace with actual API call
      // const addedSecret = await addSecret(vaultName, newSecret);
      // setSecrets([...secrets, addedSecret]);

      // Mock add
      // console.log("line 75", newSecret);
      const payload = {
        VaultData: [newSecret], // Assuming newSecret is { name: '', value: '' }
        VaultNm: vaultName,
      };
      await axios.post(
        config.REACT_APP_ENV_API_BASE + '/KeyvaultControl/postKeyVaultAdmin',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            // Add other headers here if needed, e.g. Authorization
            'Authorization': `Bearer ${sessionStorage.getItem('login')}`,
          },
        }
      );
      setIsEditing(false);
      setNewSecret({ name: '', value: '' });
      pageload ? setPageload(false) : setPageload(true);


    } catch (err) {
      setError('Failed to add secret');
    }
  };

  const handleUpdateSecret = async () => {
    try {
      const payload = {
        VaultData: [newSecret], // Assuming newSecret is { name: '', value: '' }
        VaultNm: vaultName,
      };
      await axios.post(
        config.REACT_APP_ENV_API_BASE + '/KeyvaultControl/postKeyVaultAdmin',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            // Add other headers here if needed, e.g. Authorization
            'Authorization': `Bearer ${sessionStorage.getItem('login')}`,
          },
        }
      );
      setIsEditing(false);
      setNewSecret({ name: '', value: '' });
      pageload ? setPageload(false) : setPageload(true);
    } catch (err) {
      setError('Failed to update secret');
    }
  };

  const handleDeleteSecret = async (keyName: string) => {
    if (!window.confirm('Are you sure you want to delete this secret?')) return;

    try {
      // TODO: Replace with actual API call
      // await deleteSecret(vaultName, id);
      // setSecrets(secrets.filter(s => s.id !== id));
      await axios.post(
        config.REACT_APP_ENV_API_BASE + `/KeyvaultControl/deleteKeyAdmin?VaultNm=${vaultName}&KeyName=${keyName}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('login')}`,
          },
        }
      );
      setIsEditing(false);
      setNewSecret({ name: '', value: '' });
      pageload ? setPageload(false) : setPageload(true);

    } catch (err) {
      setError('Failed to delete secret');
    }
  };

  const handleEditClick = (secret: Secret) => {
    setNewSecret({ name: secret.name, value: secret.value });
    setIsEditing(true);
  };
  // const handleEditClick = (secret: Secret) => {
  //   setNewSecret({ name: secret.name, value: secret.value });
  //   setIsEditing(true);
  //   setEditingId(secret.id)
  //   setTimeout(() => {
  //     editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //   },120);
  // };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewSecret({ name: '', value: '' });
  };

  const toggleViewSecret = (name: string) => {
    setViewedSecrets((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" p={3}>
        <Typography color="error">{error}</Typography>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/Keyvaults')}
          sx={{ mt: 2 }}
        >
          Back to Vaults
        </Button>
      </Box>
    );
  }

  return (
    <Box bgcolor="#f5f5f5" sx={{
      overflowY: 'auto',
      pr: 2,
    }}>
      <Keyvault />
      <div className='search-container' style={{ textAlign: "center", position: "relative", top: "-40px" }}>
        <TextField
          variant="outlined"
          placeholder="Search.."
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            style: {
              background: 'white',
              borderRadius: 8,
              minWidth: 300,
              width: '100%',
              maxWidth: 500,
            },
          }}
        />
      </div>

      {/* <div style={{alignItems: "center"}}><Header/>
            </div> */}
      <Box position="relative" left="70px" display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/')} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {vaultName}
        </Typography>
      </Box>

      <Card sx={{ mb: 3, scrollMarginTop: '100px', width: "1300px", position: "relative", left: "70px" }}
        ref={editFormRef}>
        <CardContent>
          <Typography variant="h6" gutterBottom >
            {isEditing ? 'Edit Secret' : 'Add New Secret'}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                label="Secret Name"
                value={newSecret.name}
                onChange={(e) => setNewSecret({ ...newSecret, name: e.target.value })}
                size="small"
                disabled={isEditing}
                InputProps={{
                  sx: { height: 40 },
                }}
                sx={{
                  width: 500,
                  '& label.Mui-focused': {
                    color: '#734bd1',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#734bd1',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Secret Value"
                value={newSecret.value}
                onChange={(e) => setNewSecret({ ...newSecret, value: e.target.value })}
                size="small"
                InputProps={{
                  sx: { height: 40 },
                }}
                sx={{
                  width: 500,
                  '& label.Mui-focused': {
                    color: '#734bd1',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#734bd1',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item>
              <Box display="flex" gap={1}>
                {isEditing ? (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleUpdateSecret}
                      disabled={!newSecret.value?.trim()}
                      sx={{
                        height: 40,
                        width: 110,
                        backgroundColor: '#734bd1',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#5e3fb0',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#d3c5f1',
                          color: '#fff',
                        },
                      }}
                    >
                      Update
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        height: 40,
                        width: 110,
                        backgroundColor: '#734bd1',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#5e3fb0',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#d3c5f1',
                          color: '#fff',
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddSecret}
                      disabled={!newSecret.name || !newSecret.value}
                      sx={{
                        height: 40,
                        width: 110,
                        backgroundColor: '#734bd1',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#5e3fb0',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#d3c5f1',
                          color: '#fff',
                        },
                      }}
                    >
                      Add
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setPageload((prev) => !prev)}
                      sx={{
                        minWidth: 100,
                        height: 40,
                        fontWeight: 'bold',
                        backgroundColor: "#734bd1",
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#5e3fb0',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#d3c5f1',
                          color: '#fff',
                        },
                      }}
                    >
                      Refresh
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ width: "1300px", position: "relative", left: "70px" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Secrets
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>

                  <TableCell sx={{ width: '30%', backgroundColor: '#d6d6d6', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ width: '30%', backgroundColor: '#d6d6d6', fontWeight: 'bold' }}>Value</TableCell>
                  <TableCell sx={{ width: '10%', backgroundColor: '#d6d6d6', fontWeight: 'bold' }}>Module</TableCell>
                  <TableCell sx={{ width: '20%', backgroundColor: '#d6d6d6', fontWeight: 'bold' }}>Repo</TableCell>
                  <TableCell sx={{ width: '15%', backgroundColor: '#d6d6d6', fontWeight: 'bold' }}>Updated DateTime</TableCell>
                  <TableCell sx={{ width: '15%', backgroundColor: '#d6d6d6', fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSecrets.length > 0 ? (
                  filteredSecrets.map((secret) => (

                    <TableRow key={secret.id} hover>
                      <TableCell
                        sx={{
                          maxWidth: 10,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <Tooltip title={secret.name} placement="top-start" arrow>
                          <span>{secret.name}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {viewedSecrets[secret.name] ? (
                          <Tooltip title={secret.value} placement="top-start" arrow>
                            <span>{secret.value}</span>
                          </Tooltip>
                        ) : (
                          <span>********</span>
                        )}
                      </TableCell>
                      <TableCell>{secret.parentName}</TableCell>
                      <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip title={secret.repoName} placement="top-start" arrow>
                          <span>{secret.repoName}</span>
                        </Tooltip></TableCell>
                      <TableCell>{formatDateTime(secret.LastUpdated)}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                          <Tooltip title={viewedSecrets[secret.name] ? 'Hide' : 'View'} placement="top" arrow>
                            <IconButton size="small" onClick={() => toggleViewSecret(secret.name)}>
                              {viewedSecrets[secret.name] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy to clipboard" placement="top" arrow>
                            <IconButton size="small" onClick={() => copyToClipboard(secret.value)}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" placement="top" arrow>
                            <IconButton size="small" onClick={() => handleEditClick(secret)} sx={{ color: '#734bd1' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" placement="top" arrow>
                            <IconButton size="small" onClick={() => handleDeleteSecret(secret.name)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No secrets found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VaultDetail;

